import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';

export type PageType =
  | 'homepage'
  | 'location'
  | 'region'
  | 'service'
  | 'services_listing'
  | 'service_areas_listing'
  | 'blog'
  | 'blog_roll'
  | 'promotions'
  | 'jobs'
  | 'reviews'
  | 'contact'
  | 'about'
  | 'team'
  | 'plans'
  | 'products';

export type HeroStyle = 'full_bleed' | 'split' | 'minimal_text' | 'stacked' | 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5';
export type LayoutVariant = 'sidebar_right' | 'full_width' | 'sidebar_left';

export interface PageDesignConfig {
  page_type: PageType;
  hero_style: HeroStyle;
  hero_overlay_opacity: number;
  hero_height: 'sm' | 'md' | 'lg';
  layout_variant: LayoutVariant;
  show_breadcrumbs: boolean;
  show_trust_badges: boolean;
  show_cta_buttons: boolean;
  show_faq_section: boolean;
  show_lead_form: boolean;
  ai_hero_image_style: string;
  ai_hero_auto_generate: boolean;
}

const DEFAULT_CONFIG: Omit<PageDesignConfig, 'page_type'> = {
  hero_style: 'full_bleed',
  hero_overlay_opacity: 0.6,
  hero_height: 'md',
  layout_variant: 'sidebar_right',
  show_breadcrumbs: true,
  show_trust_badges: true,
  show_cta_buttons: true,
  show_faq_section: true,
  show_lead_form: true,
  ai_hero_image_style: 'city_landmarks',
  ai_hero_auto_generate: true,
};

export const usePageDesign = (pageType: PageType) => {
  const { data, isLoading } = useQuery({
    queryKey: ['page-design-config', pageType],
    queryFn: async () => {
      const { data, error } = await pmsFrom('page_design_config')
        .select('*')
        .eq('page_type', pageType)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const config = {
    ...DEFAULT_CONFIG,
    ...(data ?? {}),
    page_type: pageType,
  } as PageDesignConfig;

  return {
    config,
    isLoading,
    heroStyle: config.hero_style as HeroStyle,
    layoutVariant: config.layout_variant as LayoutVariant,
    showBreadcrumbs: config.show_breadcrumbs,
    showTrustBadges: config.show_trust_badges,
    showCtaButtons: config.show_cta_buttons,
    showFaqSection: config.show_faq_section,
    showLeadForm: config.show_lead_form,
  };
};
